import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AttachmentGroupInfo, UpdateAttachmentRequest } from '../../../sdk/sdk';
import { AttachmentService } from '../../../services/attachment.service';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'app-attachment-edit-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './attachment-edit-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentEditModalComponent implements OnInit {
  // Writable signals used instead of input() because NgbModal sets values
  // imperatively via componentInstance — signal inputs are read-only.
  readonly attachmentId = signal('');
  readonly initialGroupId = signal<string | undefined>(undefined);
  readonly initialDescription = signal('');

  readonly groups = signal<AttachmentGroupInfo[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly groupsLoadError = signal(false);
  readonly saveError = signal('');

  readonly form: FormGroup;

  private readonly activeModal = inject(NgbActiveModal);
  private readonly attachmentService = inject(AttachmentService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      groupId: [''],
      description: ['', Validators.maxLength(500)],
    });
  }

  ngOnInit(): void {
    this.loadGroups();
    this.form.patchValue({
      groupId: this.initialGroupId() ?? '',
      description: this.initialDescription(),
    });
  }

  save(): void {
    if (this.form.invalid || this.isSaving()) return;

    this.isSaving.set(true);
    this.saveError.set('');

    const groupId = this.form.value.groupId || undefined;
    const description = this.form.value.description ?? '';

    const request = new UpdateAttachmentRequest({
      attachmentGroupId: groupId,
      description,
    });

    this.attachmentService
      .updateAttachment(this.attachmentId(), request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.isSaving.set(false);
          this.activeModal.close(updated);
        },
        error: (error) => {
          console.error('Error updating attachment:', error);
          this.isSaving.set(false);
          this.saveError.set('Failed to save changes. Please try again.');
        },
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private loadGroups(): void {
    this.isLoading.set(true);
    this.groupsLoadError.set(false);
    this.attachmentService
      .getAttachmentGroups()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (groups) => {
          this.groups.set(groups);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading attachment groups:', error);
          this.isLoading.set(false);
          this.groupsLoadError.set(true);
        },
      });
  }
}
